import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';
import VideoPlayerSkin from './VideoPlayerSkin';
import { MEDIA_VIEWER } from '../../../constants/staticTexts';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import VipIcon from '../../Icons/VipIcons';
import { getCachedAspectRatio, setCachedAspectRatio } from '../../../utils/mediaAspectCache';

/**
 * Universal Media Player Component
 * 
 * Supports:
 * - Images with dynamic aspect ratio
 * - Videos with playback controls
 * - Audio with waveform visualization
 * - Modal viewer with navigation
 * - Responsive design
 * - Error handling and loading states
 */

// Core Media Viewer Component
const MediaViewer = ({ 
  src, 
  type = 'image', 
  alt = MEDIA_VIEWER.DEFAULT_ALT,
  aspectRatio = null,
  onAspectRatioLoad,
  onClick,
  onPlay,
  onPause,
  onEnded,
  onTimeUpdate, // New prop for tracking video time
  mediaRef,
  showControls = false,
  isPlaying = false,
  onTogglePlay,
  poster,
  muted = true,
  autoPlay = false,
  startTime = 0, // New prop for starting video at specific time
  className,
  processingStatus = 'completed', // Processing status from backend
  isOwnMessage = false, // New prop for sender/receiver distinction
  rejectionReason, // New prop for rejection reason
  isFreePreview = true, // New prop for lock status - true means unlocked, false means locked
  showLockIcon = true, // New prop to control whether to show lock icon
  onFullscreenClick, // New prop for custom fullscreen handling (e.g., open modal)
  ...props 
}) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  // Start from a remembered ratio so a re-mounted item reserves its space before it loads
  const [calculatedAspectRatio, setCalculatedAspectRatio] = useState(() => aspectRatio || getCachedAspectRatio(src));
  const [currentSrc, setCurrentSrc] = useState(src);

  // Post images are full-size originals (often 3-4 MB / 12 MP, ~48 MB decoded). A feed holds many
  // of them and iOS Safari kills the page under memory pressure, so only images within about
  // 1.5 screens of the viewport keep their src; the rest are released.
  const containerRef = useRef(null);
  const [isNearViewport, setIsNearViewport] = useState(typeof IntersectionObserver === 'undefined');
  useEffect(() => {
    const container = containerRef.current;
    if (type !== 'image' || !container || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => setIsNearViewport(entry.isIntersecting),
      { rootMargin: '150% 0px' }
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, [type]);

  // Silent retry state
  const MAX_RETRIES = 3;
  const RETRY_DELAYS = [1000, 3000, 7000];
  const retryCount = useRef(0);
  const retryTimeoutRef = useRef(null);
  
  // Check if content is still being processed
  const isProcessing = processingStatus === 'processing' || 
                      processingStatus === 'uploading' || 
                      processingStatus === 'pending' ||
                      processingStatus === 'PENDING' ||
                      processingStatus === 'PROCESSING' ||
                      processingStatus === 'IN_PROGRESS' ||
                      processingStatus === 'UPLOADED';
  const isRejected = processingStatus === 'REJECTED';
  const isPendingReview = processingStatus === 'PENDING_REVIEW' || 
                         processingStatus === 'pending_review';
  const isFailed = processingStatus === 'MODERATION_FAILED' || 
                   processingStatus === 'error' ||
                   processingStatus === 'failed';

  const handleError = useCallback((e) => {
    if (retryCount.current < MAX_RETRIES) {
      const delay = RETRY_DELAYS[retryCount.current] || 7000;
      retryCount.current++;
      retryTimeoutRef.current = setTimeout(() => {
        setError(false);
        setLoading(true);
        setCurrentSrc('');
        requestAnimationFrame(() => setCurrentSrc(src));
      }, delay);
    } else {
      console.error('MediaViewer ERROR (retries exhausted):', {
        src: currentSrc,
        type,
        error: e.target?.error,
        retries: retryCount.current
      });
      setError(true);
      setLoading(false);
    }
  }, [currentSrc, type, src]);

  const handleManualRetry = useCallback(() => {
    retryCount.current = 0;
    setError(false);
    setLoading(true);
    setCurrentSrc('');
    requestAnimationFrame(() => setCurrentSrc(src));
  }, [src]);

  const handleLoad = useCallback((e) => {
    setError(false);
    setLoading(false);

    // Calculate aspect ratio for images and videos
    if (type === 'image' && e.target.naturalWidth && e.target.naturalHeight) {
      const ratio = e.target.naturalWidth / e.target.naturalHeight;
      setCalculatedAspectRatio(ratio);
      setCachedAspectRatio(src, ratio);
      onAspectRatioLoad?.(ratio);
    } else if (type === 'video' && e.target.videoWidth && e.target.videoHeight) {
      const ratio = e.target.videoWidth / e.target.videoHeight;
      setCalculatedAspectRatio(ratio);
      setCachedAspectRatio(src, ratio);
      onAspectRatioLoad?.(ratio);
    }
  }, [type, src, onAspectRatioLoad]);

  // Update src when prop changes
  useEffect(() => {
    setCurrentSrc(src);
    setError(false);
    setLoading(true);
    retryCount.current = 0;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
  }, [src]);

  // Cleanup retry timeout on unmount
  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const finalAspectRatio = calculatedAspectRatio || aspectRatio;

  const renderMedia = () => {
    // Show processing state for content being moderated (highest priority)
    if (isProcessing) {
      return (
        <ProcessingContainer $aspectRatio={finalAspectRatio}>
          <div className="processing-content">
            <LoadingSpinner size={24} showText={false} />
            <span>{MEDIA_VIEWER.PROCESSING_CONTENT}</span>
            <small>{MEDIA_VIEWER.PROCESSING_SUBTITLE}</small>
          </div>
        </ProcessingContainer>
      );
    }
    
    // Show failed state
    if (isFailed) {
      return (
        <ErrorContainer $aspectRatio={finalAspectRatio}>
          <div className="error-content">
            <VipIcon name="AlertIcon" size={32} />
            <span>{MEDIA_VIEWER.CONTENT_FAILED}</span>
            <small>{MEDIA_VIEWER.TRY_UPLOAD_AGAIN}</small>
          </div>
        </ErrorContainer>
      );
    }

    // Show rejected state (only to sender with rejection reason)
    if (isRejected && isOwnMessage) {
      const reason = rejectionReason || MEDIA_VIEWER.DEFAULT_REJECTION_REASON;
      return (
        <ErrorContainer $aspectRatio={finalAspectRatio}>
          <div className="error-content">
            <VipIcon name="AlertIcon" size={32} />
            <span>{MEDIA_VIEWER.CONTENT_NOT_APPROVED}</span>
            <small>{reason}</small>
          </div>
        </ErrorContainer>
      );
    }

    // Show pending review state
    if (isPendingReview) {
      return (
        <ProcessingContainer $aspectRatio={finalAspectRatio}>
          <div className="processing-content">
            <VipIcon name="ClockIcon" size={32} />
            <span>{MEDIA_VIEWER.UNDER_REVIEW}</span>
            <small>{MEDIA_VIEWER.MANUAL_REVIEW}</small>
          </div>
        </ProcessingContainer>
      );
    }

    // If content is rejected and user is not the sender, return null (show nothing)
    if (isRejected && !isOwnMessage) {
      return null;
    }
    
    // Show rejected state for content that failed moderation
    if (isRejected) {
      return (
        <ErrorContainer $aspectRatio={finalAspectRatio}>
          <div className="error-content">
            <VipIcon name="AlertIcon" size={32} />
            <span>{MEDIA_VIEWER.CONTENT_NOT_APPROVED}</span>
            <small>{MEDIA_VIEWER.DEFAULT_REJECTION_REASON}</small>
          </div>
        </ErrorContainer>
      );
    }
    
    // Show pending review state
    if (isPendingReview) {
      return (
        <ProcessingContainer $aspectRatio={finalAspectRatio}>
          <div className="processing-content">
            <VipIcon name="ClockIcon" size={32} />
            <span>{MEDIA_VIEWER.UNDER_REVIEW}</span>
            <small>{MEDIA_VIEWER.MANUAL_REVIEW}</small>
          </div>
        </ProcessingContainer>
      );
    }
    
    // Security check: Don't render media if no valid URL is provided
    // This prevents rendering locked content that doesn't have URLs from backend
    if (!currentSrc || currentSrc.trim() === '') {
      return (
        <ErrorContainer $aspectRatio={finalAspectRatio}>
          <div className="error-content">
            <VipIcon name="AlertIcon" size={40} />
            <span>{MEDIA_VIEWER.MEDIA_NOT_AVAILABLE}</span>
          </div>
        </ErrorContainer>
      );
    }

    if (error) {
      return (
        <ErrorContainer $aspectRatio={finalAspectRatio} onClick={handleManualRetry} style={{ cursor: 'pointer' }}>
          <div className="error-content">
            <VipIcon name="AlertIcon" size={40} />
            <span>{MEDIA_VIEWER.TAP_TO_RELOAD}</span>
          </div>
        </ErrorContainer>
      );
    }

    switch (type) {
      case 'image': {
        // Filter out video-related props that shouldn't be passed to img element
        const { qualityUrls, videoSources, defaultQuality, enableQualitySelection, ...imageProps } = props;
        return (
          <ImageWrapper>
            {isNearViewport && <ImageBlurBackground $src={poster || currentSrc} />}
            <MediaImage
              src={isNearViewport ? currentSrc : undefined}
              alt={alt}
              loading="lazy"
              decoding="async"
              onError={handleError}
              onLoad={handleLoad}
              onClick={onClick}
              style={{ cursor: onClick ? 'pointer' : 'default' }}
              {...imageProps}
            />
          </ImageWrapper>
        );
      }

      case 'video':
        return (
          <VideoWrapper $aspectRatio={finalAspectRatio}>
            <VideoPlayerSkin
              src={currentSrc} // Fallback single URL for backward compatibility
              videoSources={props.videoSources || []} // Array of VideoQuality objects from backend
              qualityUrls={props.qualityUrls || {}} // Simple quality mapping
              defaultQuality={props.defaultQuality || "720p"}
              enableQualitySelection={props.enableQualitySelection !== false} // Default to true
              poster={poster}
              muted={muted}
              autoPlay={autoPlay}
              startTime={startTime} // Pass start time to video player
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
              onTimeUpdate={onTimeUpdate} // Pass time update handler
              onError={handleError}
              onLoadedMetadata={handleLoad}
              onClick={onClick}
              onFullscreenClick={onFullscreenClick}
              onFullscreen={(isFullscreen) => {
                // Handle fullscreen changes if needed
                console.log('Video fullscreen:', isFullscreen);
              }}
              fillContainer={true} // Fill the wrapper container
              aspectRatio={finalAspectRatio}
              {...props}
            />
          </VideoWrapper>
        );

      case 'audio':
        return (
          <AudioContainer $aspectRatio={finalAspectRatio}>
            <audio
              ref={mediaRef}
              src={currentSrc}
              onPlay={onPlay}
              onPause={onPause}
              onEnded={onEnded}
              onError={handleError}
              onLoadedMetadata={handleLoad}
            />
            <AudioVisual>
              <VipIcon name="VolumeIcon" size={32} />
              <AudioInfo>
                <span>{MEDIA_VIEWER.AUDIO_CONTENT}</span>
                <small>{alt}</small>
              </AudioInfo>
            </AudioVisual>
            {showControls && (
              <MediaControls onClick={onTogglePlay}>
                <PlayButton>
                  {isPlaying ? <VipIcon name="PauseIcon" size={20} /> : <VipIcon name="PlayIcon" size={20} />}
                </PlayButton>
              </MediaControls>
            )}
          </AudioContainer>
        );

      default:
        return (
          <ErrorContainer $aspectRatio={finalAspectRatio}>
            <div className="error-content">
              <VipIcon name="AlertIcon" size={32} />
              <span>{MEDIA_VIEWER.UNSUPPORTED_TYPE}</span>
            </div>
          </ErrorContainer>
        );
    }
  };

  // Determine if we're in a terminal state where no media will load
  const isTerminalState = isProcessing || isFailed || isRejected || isPendingReview || 
                          error || !currentSrc || currentSrc.trim() === '';

  return (
    <MediaContainer 
      ref={containerRef}
      $aspectRatio={finalAspectRatio} 
      $loading={loading}
      $hasVideo={type === 'video'}
      className={className}
    >
      {renderMedia()}
      {loading && !isTerminalState && (
        <LoadingOverlay>
          <LoadingSpinner size={30} showText={false} />
        </LoadingOverlay>
      )}
      
      {/* Lock/Unlock Icon Overlay */}
      {showLockIcon && (
        <LockStatusOverlay>
          <LockIcon $isLocked={!isFreePreview}>
            {isFreePreview ? (
              <VipIcon name="UnlockIcon" size={16} />
            ) : (
              <VipIcon name="Lock" size={16} />
            )}
          </LockIcon>
        </LockStatusOverlay>
      )}
    </MediaContainer>
  );
};

// Styled Components
const MediaContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%; /* Fill parent */
  background: ${({ theme }) => theme.colors.surface || '#f8f9fa'};
  border-radius: 8px;
  overflow: visible; /* Changed to visible to prevent control cutoff */
  display: flex;
  align-items: center;
  justify-content: center;
  
  /* For video content, use column flex to fill height properly */
  ${({ $hasVideo }) => $hasVideo ? `
    flex-direction: column;
    align-items: stretch;
    justify-content: stretch;
    background: transparent;
    border-radius: 0;
    overflow: visible;
  ` : `
    overflow: hidden; /* Keep hidden for non-video content */
  `}
  
  ${({ $aspectRatio, $hasVideo }) => {
    if ($aspectRatio && !$hasVideo) {
      return `
        max-width: 100%;
        max-height: 100%;
      `;
    } else if (!$hasVideo) {
      return `
        max-width: 100%;
        max-height: 100%;
        min-height: 200px;
      `;
    }
  }}
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  flex: 1; /* Fill available height in flex parent */
  min-height: 0; /* Allow shrinking in flex context */
  overflow: visible; /* Allow controls to extend outside */
  
  /* Ensure video player fills the wrapper */
  > * {
    width: 100%;
    height: 100%;
  }
`;

const MediaImage = styled.img`
  position: relative;
  z-index: 1;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
`;

/* Blur background for images - fills empty space with blurred version */
const ImageBlurBackground = styled.div`
  position: absolute;
  top: -20px;
  left: -20px;
  right: -20px;
  bottom: -20px;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center;
  filter: blur(25px);
  opacity: 0.5;
  transform: scale(1.1);
  z-index: 0;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const AudioContainer = styled.div`
  position: relative;
  width: 100%;
  ${({ $aspectRatio }) => {
    if ($aspectRatio) {
      return `aspect-ratio: ${$aspectRatio};`;
    } else {
      return `
        min-height: 120px;
        height: 120px;
      `;
    }
  }}
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    ${({ theme }) => theme.colors.primary || '#007bff'}20, 
    ${({ theme }) => theme.colors.secondary || '#6c757d'}20);
  
  audio {
    display: none;
  }
`;

const AudioVisual = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  color: ${({ theme }) => theme.colors.textPrimary || '#333'};
  text-align: center;
  
  svg {
    opacity: 0.8;
    padding: 16px;
    background: ${({ theme }) => theme.colors.primary || '#007bff'}20;
    border-radius: 50%;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
  }
`;

const AudioInfo = styled.div`
  text-align: center;
  
  span {
    display: block;
    font-weight: 600;
    margin-bottom: 4px;
  }
  
  small {
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
  }
`;

const MediaControls = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  
  &:hover {
    opacity: 1;
  }
`;

const PlayButton = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: ${({ theme }) => theme.colors.textPrimary || '#333'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  &:hover {
    transform: scale(1.1);
    background: rgba(255, 255, 255, 1);
  }
`;

const ErrorContainer = styled.div`
  width: 100%;
  height: 100%;
  ${({ $aspectRatio }) => {
    if ($aspectRatio) {
      return `aspect-ratio: ${$aspectRatio};`;
    } else {
      return `min-height: 200px;`;
    }
  }}
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.bg2 || '#f8f9fa'};
  color: ${({ theme }) => theme.colors.text || '#333'};
  border-radius: 12px;
  
  .error-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    text-align: center;
    padding: 20px;
    
    svg {
      opacity: 0.85;
      color: ${({ theme }) => theme.colors.primary || '#e74c3c'};
    }
    
    span {
      font-size: 14px;
      font-weight: 600;
    }
    
    small {
      font-size: 12px;
      opacity: 0.7;
      color: ${({ theme }) => theme.colors.textSecondary || '#888'};
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background || '#f8f9fa'};
`;

const ProcessingContainer = styled.div`
  ${({ $aspectRatio }) => $aspectRatio && `
    aspect-ratio: ${$aspectRatio};
  `}
  width: 100%;
  min-height: ${({ $aspectRatio }) => $aspectRatio ? 'auto' : '200px'};
  ${({ $aspectRatio }) => !$aspectRatio && `
    height: 200px;
  `}
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.colors.background || '#f8f9fa'};
  color: ${({ theme }) => theme.colors.textSecondary || '#666'};
  border: 2px dashed ${({ theme }) => theme.colors.border || '#dee2e6'};
  
  .processing-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    text-align: center;
    
    .spinner {
      display: none;
    }
    
    svg {
      opacity: 0.7;
      color: ${({ theme }) => theme.colors.warning || '#ffc107'};
    }
    
    span {
      font-size: 14px;
      font-weight: 500;
    }
  }
`;

const LockStatusOverlay = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  pointer-events: none;
`;

const LockIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  backdrop-filter: blur(4px);
  background: ${({ theme }) => theme.colors.darkGray};
  
  svg {
    color: white;
  }
  
  @media (max-width: 768px) {
    width: 24px;
    height: 24px;
    top: 6px;
    right: 6px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
    
    small {
      font-size: 12px;
      opacity: 0.7;
      color: ${({ theme }) => theme.colors.textSecondary || '#888'};
    }
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export default MediaViewer;